import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import DevicesIcon from "@mui/icons-material/Devices";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  type UserSessionOut,
  listMySessions,
  revokeOtherSessions,
  revokeSession,
} from "@/api/sessions";

function shortenUserAgent(ua: string | null): string {
  if (!ua) return "—";
  // Pick the leading product token, e.g. "Mozilla/5.0 ..." → "Mozilla"
  // Then try to extract the browser family if present.
  const browser =
    /(Edg|Edge|OPR|Chrome|Firefox|Safari|HeadlessChrome)\/[\d.]+/.exec(ua)?.[1] ??
    "Inconnu";
  const os = /\(([^)]+)\)/.exec(ua)?.[1] ?? "";
  return os ? `${browser} · ${os.split(";")[0]}` : browser;
}

function fmtDate(iso: string | null, locale: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function SessionsPanel() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();

  const sessions = useQuery({
    queryKey: ["my-sessions"],
    queryFn: listMySessions,
    refetchOnWindowFocus: true,
  });

  const revoke = useMutation({
    mutationFn: revokeSession,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-sessions"] }),
  });

  const revokeOthers = useMutation({
    mutationFn: revokeOtherSessions,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-sessions"] }),
  });

  const rows = sessions.data ?? [];
  const othersCount = rows.filter((s) => !s.is_current).length;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              <DevicesIcon
                fontSize="small"
                sx={{ verticalAlign: "middle", mr: 1 }}
              />
              {t("sessions.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("sessions.subtitle")}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<BlockIcon />}
            onClick={() => revokeOthers.mutate()}
            disabled={revokeOthers.isPending || othersCount === 0}
          >
            {t("sessions.revokeOthers", { count: othersCount })}
          </Button>
        </Stack>

        {sessions.isLoading ? (
          <CircularProgress size={20} />
        ) : rows.length === 0 ? (
          <Alert severity="info">{t("sessions.empty")}</Alert>
        ) : (
          <List dense disablePadding>
            {rows.map((s: UserSessionOut) => (
              <ListItem
                key={s.id}
                divider
                sx={{ px: 0 }}
                secondaryAction={
                  !s.is_current && (
                    <Tooltip title={t("sessions.revoke")}>
                      <span>
                        <IconButton
                          edge="end"
                          color="error"
                          size="small"
                          onClick={() => revoke.mutate(s.id)}
                          disabled={revoke.isPending}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )
                }
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {shortenUserAgent(s.user_agent)}
                      </Typography>
                      {s.is_current && (
                        <Chip
                          size="small"
                          color="success"
                          label={t("sessions.current")}
                        />
                      )}
                    </Stack>
                  }
                  secondary={
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{ mt: 0.25, flexWrap: "wrap" }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {t("sessions.created")}: {fmtDate(s.created_at, i18n.language)}
                      </Typography>
                      {s.last_used_at && (
                        <Typography variant="caption" color="text.secondary">
                          {t("sessions.lastUsed")}: {fmtDate(s.last_used_at, i18n.language)}
                        </Typography>
                      )}
                      {s.ip_address && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: "monospace" }}
                        >
                          IP: {s.ip_address}
                        </Typography>
                      )}
                    </Stack>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
}
